import { motion } from "framer-motion";
import { CheckCircle2, Clock, Star, Zap, AlertCircle, Eye, FileText, ClipboardList, Video, Award, GitBranch } from "lucide-react";

const STEP_COLORS = {
  red: { bg: "bg-red-500", border: "border-red-500", text: "text-red-600", light: "bg-red-100" },
  yellow: { bg: "bg-amber-500", border: "border-amber-500", text: "text-amber-600", light: "bg-amber-100" },
  blue: { bg: "bg-blue-500", border: "border-blue-500", text: "text-blue-600", light: "bg-blue-100" },
  green: { bg: "bg-emerald-500", border: "border-emerald-500", text: "text-emerald-600", light: "bg-emerald-100" },
  gold: { bg: "bg-yellow-500", border: "border-yellow-500", text: "text-yellow-600", light: "bg-yellow-100" },
  purple: { bg: "bg-purple-500", border: "border-purple-500", text: "text-purple-600", light: "bg-purple-100" }
};

// Iconos específicos por paso (6 pasos del Camino al Éxito)
const STEP_SPECIFIC_ICONS = {
  1: CheckCircle2,    // Cuenta Creada
  2: FileText,        // Subir Documentos
  3: ClipboardList,   // Llenar Cuestionario
  4: Video,           // Verificación (Zoom)
  5: Award,           // DS-160 Oficial
  6: Star             // ¡Entrevista!
};

const STEP_ICONS = {
  red: AlertCircle,
  yellow: Clock,
  blue: Eye,
  green: CheckCircle2,
  gold: Star,
  purple: AlertCircle
};

// Nombres cortos y claros para cada paso (6 pasos)
const STEP_SHORT_NAMES = {
  1: "Cuenta",
  2: "Documentos",
  3: "Cuestionario",
  4: "Verificación",
  5: "DS-160 Oficial",
  6: "¡Entrevista!"
};

// Tiempo estimado por paso (en minutos)
const STEP_ESTIMATED_TIME = {
  1: null,        // Ya completado automáticamente
  2: 10,          // 10 min para subir documentos
  3: 30,          // 30 min para cuestionario
  4: 15,          // 15 min para verificación Zoom
  5: 60,          // 60 min para DS-160 oficial
  6: null         // Depende de la cita
};

// Requisitos para completar cada paso
const STEP_REQUIREMENTS = {
  2: { total: 3, label: "documentos" },  // 3 documentos requeridos
  3: { total: 11, label: "secciones" }   // 11 secciones del cuestionario
};

// Colores para modo prueba (bloqueado)
const TRIAL_STEP_COLORS = {
  bg: "bg-gray-400",
  light: "bg-gray-100",
  text: "text-gray-500",
  border: "border-gray-300"
};

export const CaminoAlExito = ({ steps, currentStep, onStepClick, documentsCount = 0, ds160Progress = 0, isTrialMode = false }) => {
  // Calcular progreso por paso
  const getStepProgress = (stepOrder) => {
    // En modo prueba no mostrar progreso
    if (isTrialMode) return null;
    
    if (stepOrder === 2) {
      const progress = Math.min((documentsCount / STEP_REQUIREMENTS[2].total) * 100, 100);
      return { progress, current: documentsCount, total: STEP_REQUIREMENTS[2].total, label: STEP_REQUIREMENTS[2].label };
    }
    if (stepOrder === 3) {
      const progress = Math.min((ds160Progress / STEP_REQUIREMENTS[3].total) * 100, 100);
      return { progress, current: ds160Progress, total: STEP_REQUIREMENTS[3].total, label: STEP_REQUIREMENTS[3].label };
    }
    return null;
  };
  return (
    <div className="card-elevated p-6 overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-navy-900">Camino al Éxito</h2>
            <p className="text-sm text-navy-500">Visa B1/B2 - Tu progreso hacia la visa</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-xs text-navy-600 font-medium">Listo</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-xs text-navy-600 font-medium">En progreso</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-xs text-navy-600 font-medium">Pendiente</span>
          </div>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="relative py-4">
        {/* Background Line */}
        <div className="absolute top-1/2 left-8 right-8 h-1.5 bg-navy-200 rounded-full -translate-y-1/2"></div>
        
        {/* Progress Line */}
        <motion.div 
          className="absolute top-1/2 left-8 h-1.5 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full -translate-y-1/2"
          initial={{ width: 0 }}
          animate={{ width: `calc(${((currentStep - 1) / (steps.length - 1)) * 100}% - 32px)` }}
          transition={{ duration: 1, ease: "easeOut" }}
        ></motion.div>

        {/* Steps */}
        <div className="relative flex justify-between px-4">
          {steps.map((step, index) => {
            const color = step.color || "red";
            const colors = STEP_COLORS[color];
            const StepIcon = STEP_SPECIFIC_ICONS[step.step_order] || STEP_ICONS[color];
            const isActive = step.step_order === currentStep;
            const isCompleted = step.status === "completed" || color === "green";
            
            // Los pasos 2 (Documentos) y 3 (DS-160) son paralelos - ambos accesibles cuando paso 1 está completado
            const isParallelStep = step.step_order === 2 || step.step_order === 3;
            
            // Verificar si el paso 1 está completado (para habilitar pasos paralelos)
            const step1Data = steps.find(s => s.step_order === 1);
            const step1IsCompleted = step1Data && (step1Data.status === "completed" || step1Data.color === "green");
            
            // EN MODO PRUEBA: Todo bloqueado hasta que pague
            // EN MODO ACTIVO: Lógica normal
            const isClickable = isTrialMode ? false : (
              isCompleted || 
              step.step_order <= currentStep || 
              (isParallelStep && step1IsCompleted)
            );
            
            // Marcar como "en progreso" si es un paso paralelo disponible (solo en modo activo)
            const isInProgress = !isTrialMode && isParallelStep && step1IsCompleted && !isCompleted;
            const shortName = STEP_SHORT_NAMES[step.step_order] || step.step_name;
            
            // Obtener progreso para pasos 2 y 3 (no en modo prueba)
            const stepProgress = getStepProgress(step.step_order);

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center relative group"
              >
                {/* Step Circle */}
                <button
                  onClick={() => isClickable && onStepClick && onStepClick(step)}
                  disabled={!isClickable || isTrialMode}
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center z-10
                    transition-all duration-300 shadow-lg
                    ${isTrialMode 
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed" 
                      : isCompleted 
                        ? "bg-emerald-500 text-white" 
                        : isInProgress 
                          ? "bg-amber-500 text-white" 
                          : `${colors.bg} text-white`
                    }
                    ${!isTrialMode && isActive ? "ring-4 ring-offset-2 ring-amber-400 scale-110" : ""}
                    ${!isTrialMode && isInProgress && !isActive ? "ring-2 ring-offset-1 ring-amber-300" : ""}
                    ${!isTrialMode && isClickable ? "cursor-pointer hover:scale-110 hover:shadow-xl" : "cursor-default opacity-60"}
                  `}
                  data-testid={`step-${step.step_order}`}
                  title={isTrialMode ? "Disponible al activar tu plan" : step.step_name}
                >
                  <StepIcon className="w-5 h-5" />
                </button>

                {/* Step Number Badge */}
                <div className={`
                  absolute -top-1 -right-1 w-6 h-6 rounded-full 
                  ${isTrialMode 
                    ? "bg-gray-200 text-gray-500" 
                    : isCompleted 
                      ? "bg-emerald-100 text-emerald-600" 
                      : colors.light + " " + colors.text
                  }
                  flex items-center justify-center text-xs font-bold
                  border-2 border-white shadow-md
                `}>
                  {!isTrialMode && isCompleted ? "✓" : step.step_order}
                </div>

                {/* Step Label - Siempre visible */}
                <div className="mt-3 text-center min-w-[80px]">
                  <p className={`
                    text-sm font-semibold leading-tight
                    ${isTrialMode 
                      ? "text-gray-400" 
                      : isActive 
                        ? "text-amber-600" 
                        : isCompleted 
                          ? "text-emerald-600" 
                          : isInProgress 
                            ? "text-amber-600" 
                            : "text-navy-700"
                    }
                  `}>
                    {shortName}
                  </p>
                  
                  {/* Mensaje de modo prueba */}
                  {isTrialMode && step.step_order === 1 && (
                    <span className="text-[10px] text-gray-400 block mt-1">Modo Prueba</span>
                  )}
                  
                  {/* Progress Bar para pasos 2 y 3 - Solo en modo activo */}
                  {!isTrialMode && stepProgress && (isInProgress || isActive) && !isCompleted && (
                    <div className="mt-2 w-full">
                      <div className="h-1.5 bg-navy-200 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${stepProgress.progress}%` }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          className={`h-full rounded-full ${
                            stepProgress.progress >= 100 ? "bg-emerald-500" : "bg-amber-500"
                          }`}
                        />
                      </div>
                      <p className="text-[10px] text-navy-500 mt-1">
                        {stepProgress.current}/{stepProgress.total} {stepProgress.label}
                      </p>
                    </div>
                  )}
                  
                  {/* Mostrar "Completo" si el progreso es 100% pero aún no está marcado como completado */}
                  {!isTrialMode && stepProgress && stepProgress.progress >= 100 && !isCompleted && (
                    <span className="text-[10px] text-emerald-600 font-medium">¡Listo!</span>
                  )}
                  
                  {/* Indicadores de estado - Solo en modo activo */}
                  {!isTrialMode && !stepProgress && isActive && (
                    <span className="text-xs text-amber-500 font-medium">← Aquí estás</span>
                  )}
                  {!isTrialMode && !stepProgress && isInProgress && !isActive && (
                    <span className="text-xs text-amber-500 font-medium">Disponible</span>
                  )}
                  {!isTrialMode && stepProgress && isActive && stepProgress.progress < 100 && (
                    <span className="text-xs text-amber-500 font-medium">← Aquí estás</span>
                  )}
                  {!isTrialMode && stepProgress && isInProgress && !isActive && stepProgress.progress < 100 && (
                    <span className="text-xs text-amber-500 font-medium">Disponible</span>
                  )}
                  
                  {/* Tiempo estimado - Solo en modo activo */}
                  {!isTrialMode && STEP_ESTIMATED_TIME[step.step_order] && !isCompleted && (isInProgress || isActive) && (
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <Clock className="w-3 h-3 text-navy-400" />
                      <span className="text-[10px] text-navy-400">~{STEP_ESTIMATED_TIME[step.step_order]} min</span>
                    </div>
                  )}
                </div>

                {/* Tooltip on hover */}
                <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20">
                  <div className="bg-navy-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                    {step.step_name}
                    {STEP_ESTIMATED_TIME[step.step_order] && !isCompleted && (
                      <span className="block text-navy-300 mt-1">
                        ⏱️ ~{STEP_ESTIMATED_TIME[step.step_order]} min
                      </span>
                    )}
                    {stepProgress && (
                      <span className="block text-navy-300 mt-1">
                        {stepProgress.current}/{stepProgress.total} {stepProgress.label}
                      </span>
                    )}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-navy-900 rotate-45"></div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Simplified Legend */}
      <div className="mt-6 pt-4 border-t border-navy-100">
        <p className="text-xs text-navy-500 text-center">
          Haz clic en cualquier paso para ver más detalles o continuar tu progreso
        </p>
      </div>
    </div>
  );
};

export default CaminoAlExito;
